import { Loader } from "@googlemaps/js-api-loader";
import {
  Card,
  Theme,
  ThemeProvider,
  Typography,
  useTheme,
} from "@material-ui/core";
import { AxiosInstance } from "axios";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { GeolocationApiFactory } from "../../common/data/service-proxies";
import { useAxiosInstance } from "../../common/hooks/useAxiosInstance";
import classes from "./CompactGoogleMap.module.css";
import { Button } from "react-admin";
import { Check } from "@material-ui/icons";
import ReactDOM from "react-dom";
import { useProLogsMapTheme } from "../../common/hooks/useProLogsMapTheme";

interface Props {
  defaultMapType?: "roadmap" | "terrain" | "satellite" | "hybrid";
  defaultZoomLevel?: number;
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  scaleControl?: boolean;
  streetViewControl?: boolean;
  rotateControl?: boolean;
  fullscreenControl?: boolean;
  centerLat?: number;
  centerLng?: number;
  onMapLoaded?: (loaded: boolean) => void;
  onAccept?: (value: SelectedMapLocation) => void;
}

interface GeolocationProps {
  lat?: number;
  lng?: number;
  axios?: AxiosInstance;
}

interface InfoWindowProps {
  lat?: number;
  lng?: number;
  theme: Theme;
  geolocation?: string | null;
  onAccept?: (value: SelectedMapLocation) => void;
}

export interface SelectedMapLocation {
  lat?: number;
  lng?: number;
  geolocation?: string;
}

const LoadingInfoWindowContent = ({ theme }: InfoWindowProps) => {
  return (
    <ThemeProvider theme={theme}>
      <div className={classes["geolocation-container"]}>
        <Typography variant="body2">Loading...</Typography>
      </div>
    </ThemeProvider>
  );
};

const GeolocationInfoWindowContent = ({
  theme,
  geolocation,
}: InfoWindowProps) => {
  return (
    <ThemeProvider theme={theme}>
      <style>
        {`.gm-style-iw-tc::after, .gm-style-iw-d, .gm-style-iw-c { background: ${theme.palette.background.paper} !important; background-color: ${theme.palette.background.paper} !important; overflow: hidden !important; }
        .gm-ui-hover-effect > span { background: ${theme.palette.primary.main} !important; }`}
      </style>
      <Card className={classes["geolocation-container"]}>
        <Typography variant="body2">
          {geolocation ??
            "Unable to find the geolocation for the selected coordinates"}
        </Typography>
        <Button disabled={!geolocation} label="Select">
          <Check />
        </Button>
      </Card>
    </ThemeProvider>
  );
};

export const CompactGoogleMapLocationSelector = (props: Props) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapStyles = useProLogsMapTheme();
  const theme: Theme = useTheme();
  const axios = useAxiosInstance();

  const [map, setMap] = useState<google.maps.Map>();
  const [geolocation, setGeolocation] = useState<string | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentLatLng, setCurrentLatLng] = useState<google.maps.LatLng | null>(
    null
  );

  const infoWindowRef = useRef<google.maps.InfoWindow>();

  const refreshGeolocation = useCallback(
    ({ lat, lng }: GeolocationProps) => {
      if (!axios) {
        return;
      }

      setLoading(true);
      GeolocationApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppGeolocationGeolocationwithradiusGet(lat, lng, 160)
        .then((response) => {
          const { distance, direction, name, state } = response.data;
          if (!name) {
            setGeolocation(null);
            return;
          }

          setGeolocation(
            !distance || distance < 2
              ? `${name} ${state}`
              : `${distance}mi ${direction} ${name}, ${state}`
          );
        })
        .finally(() => setLoading(false));
    },
    [axios]
  );

  const { onAccept } = props;
  const handleAccept = useCallback(
    (acceptValue: SelectedMapLocation) => {
      infoWindowRef.current?.close();
      if (onAccept) {
        onAccept(acceptValue);
      }
    },
    [onAccept]
  );

  useEffect(() => {
    const loader = new Loader({
      id: "googlemap",
      apiKey: "AIzaSyCUBrZAf7Q4zF_Sd-KKtxvvvH3YdDcwlbE",
      version: "quarterly",
      language: "en",
      region: "US",
      libraries: ["drawing"],
    });

    loader
      .load()
      .then(() => {
        const {
          defaultMapType = "roadmap",
          zoomControl = true,
          mapTypeControl = true,
          scaleControl = true,
          streetViewControl = false,
          rotateControl = false,
          fullscreenControl = true,
          centerLat,
          centerLng,
          onMapLoaded,
        } = props;

        const defaultZoomLevel =
          props.defaultZoomLevel ?? (centerLat && centerLng) ? 14 : 4;

        const loadedMap = new google.maps.Map(mapRef.current!, {
          center: { lat: centerLat ?? 38, lng: centerLng ?? -90 },
          mapTypeId: defaultMapType,
          zoom: defaultZoomLevel,
          disableDefaultUI: true,
          zoomControl: zoomControl,
          scaleControl: scaleControl,
          streetViewControl: streetViewControl,
          rotateControl: rotateControl,
          fullscreenControl: fullscreenControl,
          mapTypeControl: mapTypeControl,
          mapTypeControlOptions: mapTypeControl
            ? {
                style: google.maps.MapTypeControlStyle.DEFAULT,
                mapTypeIds: ["roadmap", "terrain", "satellite", "hybrid"],
              }
            : undefined,
        });

        setMap(loadedMap);
        onMapLoaded && onMapLoaded(true);
        if (centerLat && centerLng) {
          const latLng = new google.maps.LatLng({
            lat: centerLat,
            lng: centerLng,
          });
          setCurrentLatLng(latLng);
          refreshGeolocation(latLng.toJSON()!);
        } else {
          setCurrentLatLng(null);
        }
      })
      .catch((e) => console.error(e));
  }, [props, refreshGeolocation]);

  useEffect(() => {
    if (!map) {
      return;
    }

    map.setOptions({
      styles: googleMapStyles,
    });

    const clickListener = map.addListener(
      "click",
      (e: google.maps.MapMouseEvent) => {
        setCurrentLatLng(e.latLng);
        refreshGeolocation(e.latLng?.toJSON()!);
      }
    );

    return () => clickListener.remove();
  }, [map, theme, googleMapStyles, refreshGeolocation]);

  useEffect(() => {
    const getGeolocationInfoWindowContent = (
      infoWindow: google.maps.InfoWindow,
      { lat, lng, theme, geolocation }: InfoWindowProps
    ) => {
      var target = document.createElement("div");

      ReactDOM.render(
        <GeolocationInfoWindowContent
          theme={theme}
          geolocation={geolocation}
          lat={lat}
          lng={lng}
        />,
        target,
        () => {
          const rendered = target.innerHTML.replace(
            "<button ",
            `<button onclick='window.dispatchEvent(new CustomEvent("map-location-selected", { detail: { lat: ${lat}, lng: ${lng}, geolocation: "${geolocation}" }}))' `
          );

          infoWindow.setContent(rendered);
          infoWindow.setPosition(currentLatLng);
          infoWindow.open({ map });
          infoWindowRef.current?.close();
          infoWindowRef.current = infoWindow;
        }
      );
    };

    if (loading) {
      const infoWindow = new google.maps.InfoWindow();
      infoWindow.setContent(
        ReactDOMServer.renderToString(
          <LoadingInfoWindowContent theme={theme} />
        )
      );

      infoWindow.setPosition(currentLatLng);
      infoWindow.open({ map });
      infoWindowRef.current?.close();
      infoWindowRef.current = infoWindow;
    } else if (typeof geolocation !== "undefined") {
      const infoWindow = new google.maps.InfoWindow();
      const lat = currentLatLng?.lat();
      const lng = currentLatLng?.lng();
      getGeolocationInfoWindowContent(infoWindow, {
        theme,
        geolocation,
        lat,
        lng,
      });
    }
  }, [currentLatLng, geolocation, loading, map, theme, handleAccept]);

  const isCustomEvent = (
    event: Event
  ): event is CustomEvent<SelectedMapLocation> => {
    return "detail" in event;
  };

  useEffect(() => {
    const handleLocationSelected = (event: Event) => {
      if (!isCustomEvent(event)) {
        return;
      }

      handleAccept(event.detail);
    };

    window.addEventListener("map-location-selected", handleLocationSelected);
    return () =>
      window.removeEventListener(
        "map-location-selected",
        handleLocationSelected
      );
  });

  return (
    <Fragment>
      <div className="map-container" ref={mapRef}></div>
    </Fragment>
  );
};
